// Der Abschnitt "Anschriften" des Kontaktdetails (Party 360, zweiter Schnitt;
// ST-ACT-001, Querverweis ST-PTY-002 / CM-PTY-006).
//
// OFFEN wie die Kontaktwege -- Owner-Entscheidung Michael Brozat vom
// 28.09.2026: beim Versand sucht man die Anschrift wie beim Anruf die Nummer.
// Gepflegt wird weiter auf /kontakte/{id}/adressen; hier wird nur gezeigt.
//
// EIN EIGENER ABRUF (GET /parties/{id}/addresses) und damit der vierte der
// Erstansicht. Die Detailantwort traegt keine Anschriften (ADR-0056 Ziffer 1:
// eigener Leseweg wegen des Knotenetats), also laedt der Abschnitt selbst.
//
// DREI ZUSTAENDE, und keiner verkleidet sich als ein anderer (NFR-UX-005):
//   * geladen mit Anschriften: Zweck, Anschrift, Merkmale;
//   * geladen ohne Anschrift: ehrliche Leerstelle mit dem Weiterweg;
//   * nicht geladen: eine eigene Meldung mit "Erneut versuchen" -- NIE "keine
//     Anschriften", denn ein Ladefehler sagt nichts darueber, ob es welche gibt.
//     Ein 404 der Adressroute bei sichtbarer Party bekommt dieselbe Meldung:
//     der Abschnitt vermutet keine Berechtigung (CM-SEC-003).
//
// EINE STATUSREGION, im Ruhezustand leer; der Ladehinweis steht AUSSERHALB,
// wie bei Stichworten und Zugehoerigkeit -- sonst sprachen beim Oeffnen des
// Kontakts zwei Regionen zugleich.
//
// DIE ANSCHRIFT VERLAESST DIESEN ABSCHNITT NICHT: kein Log, keine Telemetrie
// (NFR-PRV-001; belegt durch die Probe, dass das Modul nichts protokolliert).

import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Subtitle1,
} from '@fluentui/react-components';

import {
  additionalAddressNotice,
  addressesHref,
  fetchAddresses,
  groupAddresses,
  normalizationLabel,
  pruefPilleSichtbar,
  usagesHeading,
  type AddressGroup,
  type AddressView,
} from '../../addresses.ts';
import { Skelett, SKELETT_ADRESSEN } from '../../shell/skelett.tsx';
import { Zustand } from '../../shell/zustand.tsx';
import { Anschrift } from '../adressen.tsx';
import { weitereOrganisationen, type ViaOrganization } from '../../via-organization.ts';
import { PlusSymbol } from './plus-symbol.tsx';
import { SperrHinweis, type SperrenFuer } from './sperren-abschnitt.tsx';
import { useKontaktStyles } from './stile.ts';
import { UebernommenerFeldname } from './ueber-organisation.tsx';
import { partyHref } from '../../parties.ts';
import { AppLink } from '../../shell/router.tsx';

export const ADDRESS_LOAD_FAILED_HEADLINE = 'Die Anschriften konnten nicht geladen werden.';
export const ADDRESS_LOAD_FAILED_NEXT_STEP = 'Das sagt nichts darüber, ob es welche gibt. Versuche es erneut.';
export const NO_ADDRESS_TEXT = 'Für diesen Kontakt ist bisher keine Anschrift hinterlegt.';

/** Eine Anschrift, bei mehreren offenen Zwecken mit "Zwecke: ..." -- wie auf der Pflegeseite. */
function Adresse({ group, gesperrt }: { readonly group: AddressGroup; readonly gesperrt?: SperrenFuer | undefined }): ReactElement {
  const own = useKontaktStyles();
  const bevorzugt = group.usages.some((usage) => usage.isPreferred);
  const pille = pruefPilleSichtbar(group.normalizationStatus);
  return (
    // Eine Zeile der Feldliste (v4 field): der Zweck links, die Anschrift rechts.
    <div className={own.feld}>
      <dt className={own.feldName}>{usagesHeading(group)}</dt>
      <dd className={own.feldWert}>
      <Anschrift lines={group.lines} countryCode={group.countryCode} />
      {/* Leer entfällt die Zeile ganz, sonst bliebe ein Abstand ohne Inhalt. */}
      {bevorzugt || pille ? (
        <span className={own.merkmale}>
          {bevorzugt ? (
            <Badge appearance="outline" color="brand">
              bevorzugt
            </Badge>
          ) : undefined}
          {/* In WORTEN, nicht als Farbe allein (NFR-ACC-001, WCAG 1.4.1). */}
          {pille ? (
            <Badge appearance="outline" color="informative">
              {normalizationLabel(group.normalizationStatus)}
            </Badge>
          ) : undefined}
        </span>
      ) : undefined}
      {/* Post kennt keinen einzelnen Weg: eine Post-Sperre trifft jede laufende Anschrift. */}
      {gesperrt === undefined ? undefined : <SperrHinweis treffer={gesperrt('postal')} />}
      </dd>
    </div>
  );
}

/**
 * Die Geschäftsanschrift der Organisation (P7 A): Zweck links, die Anschrift wie
 * auf einem Etikett -- Organisation, Person, Straße, Ort --, ohne Prüfstatus und
 * Aktionen. Die erste Zeile ist der eine Link auf die Organisation (Nachtrag
 * DESIGN 19.10. V3′); die Herkunft sagt "Geschäftsanschrift, geerbt" davor. Bis
 * je Organisation ein Block steht (E3 S3), nennt eine Caption die weiteren.
 * Eine Post-Sperre der Person gilt auch für sie (wie E6.3 im Export).
 */
function UebernommeneAdresse({
  ueber,
  name,
  gesperrt,
}: {
  readonly ueber: ViaOrganization;
  readonly name: string;
  readonly gesperrt?: SperrenFuer | undefined;
}): ReactElement | null {
  const own = useKontaktStyles();
  if (ueber.postalAddress === undefined) return null;
  // Derselbe Wortlaut wie an den Kontaktwegen.
  const weitere = weitereOrganisationen(ueber.additionalEmploymentCount);
  return (
    <div className={own.feld}>
      <dt className={own.feldName}>
        <UebernommenerFeldname label="Geschäftsanschrift" />
      </dt>
      <dd className={own.feldWert}>
        <Anschrift
          lines={[
            <AppLink key="organisation" to={partyHref(ueber.organizationPartyId)}>
              {ueber.organizationName}
            </AppLink>,
            name,
            ...ueber.postalAddress.lines,
          ]}
          countryCode={ueber.postalAddress.countryCode}
        />
        {weitere === undefined ? undefined : <Caption1 className={own.ueberOrganisation}>{weitere}</Caption1>}
        {gesperrt === undefined ? undefined : <SperrHinweis treffer={gesperrt('postal')} />}
      </dd>
    </div>
  );
}

export function Adressen({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
  name,
  nurLesen = false,
  aktion,
  gesperrt,
  ueber,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly partyId: string;
  /** Der Name der Person für die zweite Etikettzeile der übernommenen Anschrift. */
  readonly name: string;
  /** Der Kontakt ist nicht beschreibbar: kein Weg zum Anlegen. */
  readonly nurLesen?: boolean;
  /** Der Weg zur Pflegeseite am Ende der Ueberschriftenzeile (v4 h2 .end). */
  readonly aktion?: ReactNode;
  /** Die Sperren für Post (ST-CAM-001 C1a). */
  readonly gesperrt?: SperrenFuer | undefined;
  /** Was die Person von ihrer Organisation übernimmt (P7 A); steht nach den eigenen Anschriften. */
  readonly ueber?: ViaOrganization | undefined;
}): ReactElement {
  const own = useKontaktStyles();
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'fehler'>('laedt');
  const [adressen, setAdressen] = useState<readonly AddressView[]>([]);
  const [weitere, setWeitere] = useState(0);
  const laufRef = useRef(0);
  // "Anschriften erneut laden" verschwindet waehrend des Ladens. Ohne Ausweichziel
  // fiele der Fokus auf das Dokument (WCAG 2.4.3; Codex-Cloud-Review #277):
  // danach steht er wieder auf dem Knopf, wenn es erneut scheitert, sonst auf
  // der Ueberschrift des Abschnitts. Nur nach einem ausdruecklichen Neuladen --
  // der erste Abruf beim Oeffnen des Kontakts zieht den Fokus nicht an sich.
  const nachNeuladen = useRef(false);
  const abschnittRef = useRef<HTMLElement | null>(null);
  const neuLadenRef = useRef<HTMLDivElement | null>(null);

  const laden = useCallback(async () => {
    const meinLauf = laufRef.current + 1;
    laufRef.current = meinLauf;
    const gueltig = (): boolean => laufRef.current === meinLauf;
    setStatus('laedt');
    try {
      const token = await accessToken();
      if (!gueltig()) {
        return;
      }
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchAddresses({ baseUrl: apiBaseUrl, accessToken: token, partyId });
      if (!gueltig()) {
        return;
      }
      if (result.outcome !== 'loaded') {
        setStatus('fehler');
        return;
      }
      setAdressen(result.data.postalAddresses);
      setWeitere(result.data.additionalPostalAddressCount);
      setStatus('geladen');
    } catch {
      if (gueltig()) {
        setStatus('fehler');
      }
    }
  }, [accessToken, apiBaseUrl, onReauthenticate, partyId]);

  useEffect(() => {
    void laden();
    return () => {
      laufRef.current += 1;
    };
  }, [laden]);

  useEffect(() => {
    if (!nachNeuladen.current || status === 'laedt') {
      return;
    }
    nachNeuladen.current = false;
    if (status === 'fehler') {
      neuLadenRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    } else {
      abschnittRef.current?.querySelector<HTMLHeadingElement>('h2')?.focus();
    }
  }, [status]);

  const zuviel = additionalAddressNotice(weitere);
  const uebernommen = ueber?.postalAddress === undefined ? undefined : ueber;

  return (
    <section className={own.gruppe} aria-labelledby="adressen" ref={abschnittRef}>
      <div className={own.abschnittKopf} data-abschnittkopf="">
        <Subtitle1 as="h2" id="adressen" tabIndex={-1}>
          Anschriften
        </Subtitle1>
        {aktion}
      </div>
      {status === 'laedt' ? <Skelett ladesatz="Anschriften werden geladen" zeilen={SKELETT_ADRESSEN} /> : undefined}
      <div role="status" aria-live="polite" aria-label="Status: Anschriften">
        {status === 'fehler' ? (
          <div ref={neuLadenRef}>
            <Zustand
              art="fehler"
              // Steht eine übernommene Anschrift darunter, sagt der Titel "eigene", sonst wirkte sie wie ein Widerspruch.
              titel={uebernommen === undefined ? ADDRESS_LOAD_FAILED_HEADLINE : 'Die eigenen Anschriften konnten nicht geladen werden.'}
              saetze={[ADDRESS_LOAD_FAILED_NEXT_STEP]}
              eingebettet
              haupt={{
                label: 'Anschriften erneut laden',
                onClick: () => {
                  nachNeuladen.current = true;
                  void laden();
                },
              }}
            />
          </div>
        ) : undefined}
      </div>
      {status === 'geladen' && adressen.length === 0 && uebernommen === undefined ? (
        <Zustand
          art="leer"
          titel={nurLesen ? 'Keine Anschrift hinterlegt.' : NO_ADDRESS_TEXT}
          saetze={[]}
          eingebettet
          {...(nurLesen ? {} : { haupt: { label: 'Anschrift hinzufügen', href: addressesHref(partyId), icon: <PlusSymbol /> } })}
        />
      ) : undefined}
      {/* Die übernommene Anschrift steht unabhängig vom eigenen Abruf fest: sie bleibt auch
          neben dessen Ladefehler stehen (Entscheidung in ST-PTY-002). */}
      {(status === 'geladen' && adressen.length > 0) || (status !== 'laedt' && uebernommen !== undefined) ? (
        <dl className={own.feldliste}>
          {status === 'geladen'
            ? groupAddresses(adressen).map((group) => <Adresse key={group.addressId} group={group} gesperrt={gesperrt} />)
            : undefined}
          {uebernommen === undefined ? undefined : <UebernommeneAdresse ueber={uebernommen} name={name} gesperrt={gesperrt} />}
        </dl>
      ) : undefined}
      {status === 'geladen' && zuviel !== undefined ? <Caption1 className={own.hinweis}>{zuviel}</Caption1> : undefined}
    </section>
  );
}
